import { dehydrate, QueryClient, useMutation, useQuery, UseQueryResult } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { V3GenericErrorHandler } from "../../api/error-handler";
import { subscribe } from "../../api/subscribe";
import { ContactUser } from "../../api/user";
import { withGlobalPageSpinner } from "../../components/GlobalPageSpinner";
import { ContactUserFormik } from "../../components/V3ContactUser";
import { ContentDetailsPublicPageLayout } from "./ContentDetailsPublicPageLayout";
import { getPublicUserClient, getProfileClient, getProfileServer, getPublicUserServer } from "../../utils/getAuthor";
import { getPublicContentItemClient } from "../../utils/query-utils/getContentItem";
import {
    ContentDetailType,
    ProfileUserResponse,
    PublicContentShareStatusResponse,
    PublicUserResponse,
    ShareStatus,
    SmartShareLinkResponse,
    SmartShareType,
    Visibility
} from "@authory-packages/core";
import { NextPageContext } from "next";
import { getArticleShareStatus, getPublicSmartShareArticle, getPublicArticle } from "../../api/articles";
import { getCollectionShareStatus } from "../../api/collections";
import { serverSideErrorResponse, statusResponse, unavailableResponse } from "../../utils/unavailableResource";
import { isHiddenAsSpam } from "../../utils/isHiddenAsSpam";
import { Errors } from "../../types/api";
import { getSharedContentItemQuerie, getSmartSharesLinkQuerie } from "../../types/queries";
import { UNAVAILABLE_REASON, UNAVAILABLE_RESOURCE_TYPE } from "../../types/unavailable_resource_type";
import { getShareCollectionServer } from "../../utils/query-utils/getCollection";
import { useRouter } from "next/router";
import { SmartSessionTracker } from "../../smart-tracking";
import { useEffect, useState } from "react";
import { getSmartShareLink } from "../../api/smart-share";

interface V3ContentItemPublicPageLayoutContainerProps {
    isStandaloneCollectionView: boolean;
    isStandaloneContentItemView: boolean;
    contentItemShareStatus?: PublicContentShareStatusResponse;
    profileShareStatus?: ShareStatus;
    useArticleSmartShareEndpoint: boolean;
    useProfileSmartShareEndpoint: boolean;
}

export const V3ContentItemPublicPageLayoutContainer = ({
    isStandaloneCollectionView,
    isStandaloneContentItemView,
    contentItemShareStatus,
    profileShareStatus,
    useArticleSmartShareEndpoint,
    useProfileSmartShareEndpoint
}: V3ContentItemPublicPageLayoutContainerProps) => {
    const router = useRouter();
    const smartShareUrl = router.query.smartShareURL as string;
    const [smartSessionTracker, setSmartSessionTracker] = useState<SmartSessionTracker | null>(null);

    // Get the author
    const { author, userSmartShareLink } =
        isStandaloneContentItemView || isStandaloneCollectionView
            ? getPublicUserClient()
            : getProfileClient(profileShareStatus!, useProfileSmartShareEndpoint);

    const { item, contentSmartShareLink } = getPublicContentItemClient(
        isStandaloneContentItemView,
        useArticleSmartShareEndpoint,
        contentItemShareStatus
    );

    let collectionSmartShareLink: UseQueryResult<SmartShareLinkResponse, Error> | null = null;

    if (isStandaloneCollectionView && !!smartShareUrl) {
        // if we are on a collection content item page, we also need to the smart share collection
        const smartShareLinkQuerie = getSmartSharesLinkQuerie(author.data?.slug!, smartShareUrl, SmartShareType.Collection);

        collectionSmartShareLink = useQuery({
            queryKey: smartShareLinkQuerie,
            queryFn: () => getSmartShareLink(smartShareLinkQuerie[1], smartShareLinkQuerie[2], smartShareLinkQuerie[3]),
            throwOnError: false,
            enabled: author.isSuccess
        });
    }

    const smartShareLinkToUse = userSmartShareLink || collectionSmartShareLink || contentSmartShareLink;

    useEffect(() => {
        if (author.isSuccess && smartShareUrl && author.data?.slug && smartShareLinkToUse?.data?.active && smartSessionTracker === null) {
            setSmartSessionTracker(new SmartSessionTracker(smartShareLinkToUse?.data?.uid, author.data?.slug));
        }
    }, [author.isSuccess]);

    // Contact User Mutation
    const contactUserMutation = useMutation({
        mutationFn: ContactUser,
        onError: V3GenericErrorHandler(toast.error)
    });

    const subcribeMutation = useMutation({
        mutationFn: subscribe,
        onError: V3GenericErrorHandler(toast.error)
    });

    const onSubscribeFormSubmit = async (email: string) => {
        if (!author.data) return;
        await subcribeMutation.mutateAsync({ email, userId: author.data?.slug });
    };

    const onContactUserSubmit = async (data: ContactUserFormik) => {
        if (!author.data) return;

        await contactUserMutation.mutateAsync({ userSlug: author.data?.slug, data });
    };

    return (
        <>
            {withGlobalPageSpinner(
                author.isLoading,
                <ContentDetailsPublicPageLayout
                    author={author.data!}
                    content={item.data}
                    onContactUserSubmit={onContactUserSubmit}
                    isStandaloneContentItemView={isStandaloneContentItemView}
                    isStandaloneCollectionView={isStandaloneCollectionView}
                    onSubscribeFormSubmit={onSubscribeFormSubmit}
                    smartShareUrl={smartShareUrl}
                />
            )}
        </>
    );
};

export const getServerSidePropsWithContentItem =
    (isSmartSharePage: boolean, isProfilePage: boolean, isCollectionPage: boolean) => async (ctx: NextPageContext) => {
        const queryClient = new QueryClient();
        let author: ProfileUserResponse | PublicUserResponse | undefined = undefined;

        try {
            const authorSlug = ctx.query.author as string;

            // Get public user first
            const publicUser = await getPublicUserServer(queryClient, ctx);
            author = publicUser;

            if (!publicUser) return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.ARTICLE, UNAVAILABLE_REASON.NOT_FOUND);
            // A plain 404 with nothing dehydrated, so the page html carries none of the account's data
            if (isHiddenAsSpam(publicUser)) return statusResponse(ctx, Errors.NotFound);

            // Content item accessed via profile page
            if (isProfilePage) {
                const isNotShared = publicUser && publicUser.profileShareStatus === ShareStatus.Unpublished;

                if (!isSmartSharePage && isNotShared) {
                    return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.PROFILE, UNAVAILABLE_REASON.NOT_SHARED);
                }

                // -> profile standalone
                // -> smart share profile
                const fetchProfileOnSSR = publicUser.profileShareStatus === ShareStatus.Published || isSmartSharePage;
                if (fetchProfileOnSSR) {
                    const profileServerRes = await getProfileServer(queryClient, ctx, isSmartSharePage);

                    author = profileServerRes.author;

                    if (isSmartSharePage && profileServerRes.smartShareLink && !profileServerRes.smartShareLink.active) {
                        return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.PROFILE, UNAVAILABLE_REASON.NOT_SHARED);
                    }
                }
            }

            // Content item accessed via collection page
            let collectionShareStatus;
            const collectionAccessKey = ctx.query.collectionAccessKey as string;

            if (isCollectionPage) {
                // -> collection standalone
                // -> collection via profile
                // -> smart collection standalone
                // -> smart collection profile

                if (!isSmartSharePage && !isProfilePage) {
                    collectionShareStatus = await getCollectionShareStatus(authorSlug, collectionAccessKey);
                }

                if (!isSmartSharePage && !!collectionShareStatus && collectionShareStatus.shareStatus === ShareStatus.Unpublished) {
                    return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.COLLECTION, UNAVAILABLE_REASON.NOT_SHARED);
                }

                const fetchCollectionDataOnSSR =
                    (!!collectionShareStatus && !collectionShareStatus.isPasswordProtected) || isProfilePage || isSmartSharePage;

                if (fetchCollectionDataOnSSR) {
                    const collServerRes = await getShareCollectionServer(queryClient, ctx, isSmartSharePage && !isProfilePage);

                    if (!isProfilePage && !!collServerRes.smartShareLink && !collServerRes.smartShareLink.active) {
                        return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.COLLECTION, UNAVAILABLE_REASON.NOT_SHARED);
                    }
                }
            }

            const contentItemAccessKey = ctx.query.contentItemAccessKey as string;
            let contentItemShareStatus: PublicContentShareStatusResponse | null = null;
            const token = ctx.query.c ? (ctx.query.c as string) : null;

            // Validate content item share if standalone page
            if (!isSmartSharePage && !isProfilePage && !isCollectionPage) {
                contentItemShareStatus = await getArticleShareStatus(authorSlug, contentItemAccessKey, token);

                if (
                    contentItemShareStatus.shareStatus === ShareStatus.Unpublished &&
                    contentItemShareStatus.effectiveVisibility !== Visibility.Public
                ) {
                    return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.ARTICLE, UNAVAILABLE_REASON.NOT_SHARED);
                }
            }

            const fetchContentItemOnSSR =
                (!!contentItemShareStatus && !contentItemShareStatus.isPasswordProtected) || isProfilePage || isCollectionPage || isSmartSharePage;

            if (fetchContentItemOnSSR) {
                const smartShareUrl = ctx.query.smartShareURL as string;
                const useSmartShareEndpoint = isSmartSharePage && !isProfilePage && !isCollectionPage;
                const sharedItemAccessKey = useSmartShareEndpoint ? smartShareUrl : contentItemAccessKey;
                const itemQueryQuery = getSharedContentItemQuerie(authorSlug, sharedItemAccessKey, "");

                const article = await queryClient.fetchQuery({
                    queryKey: itemQueryQuery,
                    queryFn: () =>
                        useSmartShareEndpoint
                            ? getPublicSmartShareArticle(itemQueryQuery[1]!, itemQueryQuery[2]!)
                            : getPublicArticle(itemQueryQuery[1]!, itemQueryQuery[2]!, itemQueryQuery[3]!, token)
                });

                if (useSmartShareEndpoint) {
                    const smartShareLinkQuerie = getSmartSharesLinkQuerie(authorSlug, smartShareUrl, SmartShareType.ContentItem);
                    const smartShareLink = await queryClient.fetchQuery({
                        queryKey: smartShareLinkQuerie,
                        queryFn: () => getSmartShareLink(smartShareLinkQuerie[1], smartShareLinkQuerie[2], smartShareLinkQuerie[3])
                    });

                    if (!smartShareLink.active) {
                        return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.ARTICLE, UNAVAILABLE_REASON.NOT_SHARED);
                    }
                }

                // Edge case for legacy links without UUID
                const legacyEmpty = article.article.contentDetail.type === ContentDetailType.Empty;

                if (legacyEmpty) {
                    return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.ARTICLE, UNAVAILABLE_REASON.NOT_SHARED);
                }
            }

            return {
                props: {
                    dehydratedState: dehydrate(queryClient),
                    author,
                    profileShareStatus: publicUser?.profileShareStatus,
                    collectionShareStatus: collectionShareStatus || null,
                    collectionAccessKey: collectionAccessKey || "",
                    contentItemAccessKey: contentItemAccessKey || null,
                    contentItemShareStatus
                }
            };
        } catch (error) {
            return serverSideErrorResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.ARTICLE, error);
        }
    };
